<template>
    <b-modal
        id="combined-student-evaluation-overview-modal"
        v-model="modal"
        modal-class="apprentice-default-modal"
        size="lg"
        title="Leerlingbespreking overzicht"
        ok-only
        :ok-title="$t('common.close')"
        ok-variant="primary"
        @ok="onOk">
        <div class="student-evaluation-overview">
            <div class="date">
                <h3 class="title">{{ selectedStudentEvaluation.text }}</h3>

                <div class="seasons-and-evaluation-dates">
                    <b-form-group
                        label="Schooljaar"
                        label-for="seasons-select">
                        <b-select
                            class="apprentice-primary-select season-select"
                            id="seasons-select"
                            v-model="selectedSeason"
                            :options="seasons"
                            :disabled="seasons.length < 3">
                        </b-select>
                    </b-form-group>

                    <b-form-group
                        label="Datum"
                        label-for="date-select">
                        <b-select
                            class="apprentice-primary-select evaluation-date-select"
                            id="date-select"
                            v-model="selectedDate"
                            :disabled="dates.length === 1"
                            :allow-empty="false"
                            :options="dates">
                        </b-select>
                    </b-form-group>
                </div>
            </div>

            <div class="personal-goals">
                <header>
                    <h4 class="title">Leerdoelen</h4>
                </header>

                <ul class="personal-goals">
                    <li
                        class="personal-goal"
                        v-for="personalGoal of personalGoalsIn">
                        {{ personalGoal.name }}
                    </li>
                </ul>
            </div>

            <div
                class="evaluation"
                v-if="dates.length > 1">
                <div class="educational">
                    <h4 class="title">Didactisch</h4>
                    <design2021-apprentice-display-html-component v-html="selectedStudentEvaluation.educational_text">
                    </design2021-apprentice-display-html-component>
                </div>

                <div class="pedagogical">
                    <h4 class="title">Pedagogisch</h4>
                    <design2021-apprentice-display-html-component v-html="selectedStudentEvaluation.pedagogical_text">
                    </design2021-apprentice-display-html-component>
                </div>
            </div>

            <div
                class="not-found with-header attachments-list"
                v-else>
                <div class="icon">
                    <i class="fas fa-exclamation-circle"></i>
                </div>
                <div class="alert-notice">Er zijn geen besprekingen gevonden.</div>
            </div>
        </div>
    </b-modal>
</template>

<script>
import ApprenticeEditor from '../../../helpers/ApprenticeEditor';
import Design2021ApprenticeDisplayHtmlComponent from '../../common/design-2021-apprentice-display-html-component.vue';

export default {
    name: 'design-2021-combined-student-evaluation-overview-modal-component',
    components: { Design2021ApprenticeDisplayHtmlComponent },
    props: {
        personalGoalsIn: {
            type: Array,
            required: true,
        },
        studentEvaluationsIn: {
            type: Array,
            required: false,
            default: [],
        },
        seasonsIn: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            selectedDate: this.studentEvaluationsIn.length > 0 ? this.studentEvaluationsIn[0].id : null,
            pedagogicalText: '',
            educationalText: '',
            selectedSeason: null,
            modal: false,
        };
    },

    mounted() {
        if (this.seasons.length > 1 && this.selectedStudentEvaluation) {
            for (let i = 1; i <= this.seasons.length; i++) {
                const seasonYear = this.seasons[i].season.split('-');
                const evaluationDate = new Date(this.selectedStudentEvaluation.created_at);
                if (
                    evaluationDate.getTime() >= new Date(this.seasons[i].start).getTime() &&
                    parseInt(evaluationDate.getFullYear()) <= seasonYear.splice(-1).pop()
                ) {
                    this.selectedSeason = this.seasons[i].id;
                    break;
                }
            }
        }
    },

    computed: {
        dates() {
            let dates = this.studentEvaluationsIn.map((studentEvaluation) => {
                studentEvaluation.value = studentEvaluation.id;
                studentEvaluation.text = new Date(studentEvaluation.created_at)
                    .toLocaleDateString('nl-NL')
                    .replace(/\//g, '-');

                return studentEvaluation;
            });

            if (this.selectedSeason) {
                const pos = this.seasonsIn
                    .map((season) => season.id)
                    .findIndex((seasonId) => seasonId === this.selectedSeason);

                if (pos && pos !== this.seasonsIn.length - 1) {
                    dates = dates.filter((date) => {
                        const evoDate = new Date(date.created_at);

                        if (
                            evoDate.getTime() >= new Date(this.seasonsIn[pos].start).getTime() &&
                            evoDate.getTime() <= new Date(this.seasonsIn[pos + 1].start).getTime()
                        ) {
                            return date;
                        }
                    });
                } else {
                    const years = this.seasonsIn[pos].season.split('-');
                    dates = dates.filter((date) => {
                        const evoDate = new Date(date.created_at);

                        if (
                            evoDate.getTime() >= new Date(this.seasonsIn[pos].start).getTime() &&
                            parseInt(evoDate.getFullYear()) <= years.splice(-1).pop()
                        ) {
                            return date;
                        }
                    });
                }
            }

            if (dates.length === 1) {
                this.selectedDate = dates[0].id;
            }

            dates.unshift({
                value: null,
                text: 'Kies een datum',
                disabled: true,
            });
            return dates;
        },

        selectedStudentEvaluation() {
            let existingDate = this.studentEvaluationsIn.find(
                (studentEvaluation) => studentEvaluation.id === this.selectedDate,
            );

            return existingDate
                ? existingDate
                : {
                      educational_text: '',
                      pedagogical_text: '',
                      id: null,
                      text: null,
                      value: null,
                  };
        },

        seasons() {
            let seasons = [];

            if (!this.seasonsIn.length) {
                return [];
            }

            this.seasonsIn.reverse();

            seasons = this.seasonsIn.filter((season, index, seasonsArray) => {
                let found = false;

                for (let i = 0; i < this.studentEvaluationsIn.length; i++) {
                    const startDate = new Date(season.start);
                    const evaluationDate = new Date(this.studentEvaluationsIn[i].created_at);

                    found = false;

                    if (this.seasonsIn.length !== index + 1) {
                        const endDate = new Date(seasonsArray[index + 1].start);

                        if (
                            evaluationDate.getTime() >= startDate.getTime() &&
                            evaluationDate.getTime() <= endDate.getTime()
                        ) {
                            found = true;
                            break;
                        }
                    } else {
                        const years = season.season.split('-');
                        if (
                            evaluationDate.getTime() >= startDate.getTime() &&
                            parseInt(evaluationDate.getFullYear()) <= years.splice(-1).pop()
                        ) {
                            found = true;
                            break;
                        }
                    }
                }

                season.text = season.season;
                season.value = season.id;
                return found && season;
            });

            seasons.unshift({
                value: null,
                text: 'Kies een schooljaar',
                disabled: true,
            });

            return seasons;
        },
    },

    methods: {
        onOk() {
            this.$vbModal('combined-student-evaluation-overview-modal').hide();
        },

        show() {
            this.selectedDate = this.studentEvaluationsIn.length > 0 ? this.studentEvaluationsIn[0].id : null;
            this.modal = true;
        },
    },
};
</script>

<style scoped lang="scss">
.date {
    display: flex;
    align-items: center;
    justify-content: space-between;
    column-gap: $grid-gutter-width;
    margin-bottom: $grid-gutter-width;
}

.personal-goals {
    margin-bottom: $grid-gutter-width;
}

.seasons-and-evaluation-dates {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    column-gap: map-get($grid-gutter-spacing, 'fifteen');

    select {
        width: 100%;
        margin-bottom: 0;
    }
}

.educational,
.pedagogical {
    margin-bottom: map-get($grid-gutter-spacing, 'forty-two');

    .title {
        margin-bottom: map-get($grid-gutter-spacing, 'fiften');
    }

    .display-html-content {
        font-size: $base-font-size;
        font-weight: $font-weight-medium;
        color: $text-muted-color;
        line-height: 1.3;
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .seasons-and-evaluation-dates {
        select {
            width: 150px;
        }
    }
}
</style>
